import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  WifiIcon,
  SignalIcon,
  ClockIcon,
  StopCircleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { wifiSpeedAPI, venues } from '../../services/api';

// ===== CONFIGURATION =====
const THROTTLE_CONFIG = {
  minUpdateInterval: 50,
  animationDuration: 150,
  easingFunction: (t) => 1 - Math.pow(1 - t, 3), // cubic ease-out
};

const NDT_SERVER_BASE = 'https://speedtest-vn.honestlee.app:4443';
const NDT_WS_BASE = 'wss://speedtest-vn.honestlee.app:4443';

// ===== HELPER FUNCTIONS =====
const getSpeedQuality = (speed, t) => {
  if (speed >= 50) return { label: t('review.ratingExcellent'), color: '#10b981', emoji: '🚀' };
  if (speed >= 25) return { label: t('review.ratingGood'), color: '#f59e0b', emoji: '👍' };
  if (speed >= 10) return { label: t('review.facetOpt.average'), color: '#f97316', emoji: '⚡' };
  return { label: t('review.ratingPoor'), color: '#ef4444', emoji: '🐌' };
};

const calculateQualityScore = (download, upload, latency) => {
  const downloadScore = Math.min(download / 100, 1) * 40;
  const uploadScore = Math.min(upload / 50, 1) * 30;
  const latencyScore = Math.max((100 - latency) / 100, 0) * 30;
  return Math.round(downloadScore + uploadScore + latencyScore);
};

const getCategory = (score) => {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
};

const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let model = 'Unknown';
  let os = 'Unknown';
  let browser = 'Unknown';

  if (userAgent.includes('Android')) {
    os = 'Android';
    model = userAgent.match(/Android\s([^;]+)/)?.[1] || 'Android Device';
  } else if (userAgent.includes('iPhone')) {
    os = 'iOS';
    model = 'iPhone';
  } else if (userAgent.includes('iPad')) {
    os = 'iOS';
    model = 'iPad';
  } else if (userAgent.includes('Windows')) {
    os = 'Windows';
    model = 'PC';
  } else if (userAgent.includes('Mac')) {
    os = 'macOS';
    model = 'Mac';
  }

  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edg')) {
    browser = 'Edge';
  }

  return { model, os, browser };
};

// ===== SPEEDOMETER GAUGE COMPONENT =====
const SpeedometerGauge = ({ value = 0, maxValue = 1000, label = "Download" }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const safeValue = typeof value === 'number' && isFinite(value) ? value : 0;
  
  const gaugeSize = typeof window !== 'undefined' && window.innerWidth < 480 ? 320 : 420;
  const centerX = gaugeSize / 2;
  const centerY = gaugeSize / 2 + 40;
  const radius = gaugeSize * 0.32;
  
  const startAngle = 135;
  const endAngle = 45;
  const totalAngle = 270;
  
  const percentage = Math.min(Math.max(safeValue / maxValue, 0), 1);
  const needleAngle = startAngle + (totalAngle * percentage);
  
  const needleRad = ((needleAngle - 90) * Math.PI) / 180;
  const needleLength = radius * 0.7;
  const needleEndX = centerX + needleLength * Math.cos(needleRad);
  const needleEndY = centerY + needleLength * Math.sin(needleRad);
  
  const createArc = (start, end, rad = radius) => {
    const startRad = ((start - 90) * Math.PI) / 180;
    const endRad = ((end - 90) * Math.PI) / 180;
    const x1 = centerX + rad * Math.cos(startRad);
    const y1 = centerY + rad * Math.sin(startRad);
    const x2 = centerX + rad * Math.cos(endRad);
    const y2 = centerY + rad * Math.sin(endRad);
    const largeArc = end - start > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${rad} ${rad} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const speedMarkers = [
    { value: 0, showLabel: true },
    { value: 5, showLabel: true },
    { value: 10, showLabel: true },
    { value: 50, showLabel: true },
    { value: 100, showLabel: true },
    { value: 250, showLabel: true },
    { value: 500, showLabel: true },
    { value: 750, showLabel: true },
    { value: 1000, showLabel: true }
  ];
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      margin: '30px 0',
      position: 'relative',
      width: '100%',
      maxWidth: `${gaugeSize}px`,
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      <svg 
        width={gaugeSize} 
        height={gaugeSize * 0.7} 
        style={{ 
          overflow: 'visible',
          maxWidth: '100%',
          height: 'auto',
          display: 'block'
        }}
        viewBox={`0 0 ${gaugeSize} ${gaugeSize * 0.7}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="activeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{stopColor:'#00d4ff', stopOpacity:1}} />
            <stop offset="50%" style={{stopColor:'#00b8d4', stopOpacity:1}} />
            <stop offset="100%" style={{stopColor:'#0097a7', stopOpacity:1}} />
          </linearGradient>
          
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          <linearGradient id="needleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{stopColor:'#ffffff', stopOpacity:0.3}} />
            <stop offset="100%" style={{stopColor:'#d0d0d0', stopOpacity:1}} />
          </linearGradient>
        </defs>
        
        <path
          d={createArc(startAngle, startAngle + totalAngle)}
          fill="none"
          stroke="rgba(55, 71, 95, 0.4)"
          strokeWidth={gaugeSize * 0.045}
          strokeLinecap="round"
        />
        
        <motion.path
          d={createArc(startAngle, needleAngle)}
          fill="none"
          stroke="url(#activeGradient)"
          strokeWidth={gaugeSize * 0.045}
          strokeLinecap="round"
          filter="url(#glow)"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
        
        {speedMarkers.map((marker, i) => {
          const markerPercentage = marker.value / maxValue;
          const angle = startAngle + (totalAngle * markerPercentage);
          const rad = ((angle - 90) * Math.PI) / 180;
          
          const innerRadius = radius - 5;
          const outerRadius = radius + 5;
          
          const x1 = centerX + innerRadius * Math.cos(rad);
          const y1 = centerY + innerRadius * Math.sin(rad);
          const x2 = centerX + outerRadius * Math.cos(rad);
          const y2 = centerY + outerRadius * Math.sin(rad);
          
          const textRadius = radius + 32;
          const textX = centerX + textRadius * Math.cos(rad);
          const textY = centerY + textRadius * Math.sin(rad);
          
          return (
            <g key={i}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="rgba(150, 160, 180, 0.6)"
                strokeWidth={marker.value === 0 || marker.value === maxValue ? 2.5 : 1.5}
                strokeLinecap="round"
              />
              
              {marker.showLabel && (
                <text
                  x={textX}
                  y={textY}
                  fontSize={gaugeSize * 0.038}
                  fill="rgba(150, 160, 180, 0.85)"
                  fontWeight="600"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                >
                  {marker.value}
                </text>
              )}
            </g>
          );
        })}
        
        <circle 
          cx={centerX} 
          cy={centerY} 
          r={gaugeSize * 0.022} 
          fill="#2a3f5f"
          stroke="rgba(255, 255, 255, 0.15)"
          strokeWidth="2"
        />
        
        <motion.line
          x1={centerX}
          y1={centerY}
          x2={centerX}
          y2={centerY}
          stroke="url(#needleGradient)"
          strokeWidth={gaugeSize * 0.007}
          strokeLinecap="round"
          filter="url(#glow)"
          animate={{
            x2: needleEndX,
            y2: needleEndY,
          }}
          transition={{
            type: "tween",
            ease: "easeOut",
            duration: 0.6
          }}
        />
        
        <motion.circle
          cx={centerX}
          cy={centerY}
          r={gaugeSize * 0.012}
          fill="#00d4ff"
          filter="url(#glow)"
          animate={{
            cx: needleEndX,
            cy: needleEndY,
          }}
          transition={{
            type: "tween",
            ease: "easeOut",
            duration: 0.6
          }}
        />
      </svg>
      
      <div style={{ 
        textAlign: 'center',
        marginTop: '15px',
        position: 'relative',
        zIndex: 10,
        width: '100%'
      }}>
        <motion.div
          style={{
            fontSize: 'clamp(52px, 11vw, 80px)',
            fontWeight: '300',
            color: '#ffffff',
            marginBottom: '8px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            letterSpacing: '-2px',
            lineHeight: '1'
          }}
          key={Math.floor(displayValue)}
        >
          {displayValue.toFixed(2)}
        </motion.div>
        <div style={{ 
          fontSize: 'clamp(15px, 3.5vw, 20px)', 
          color: 'rgba(150, 160, 180, 0.9)', 
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          marginTop: '5px'
        }}>
          <span style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: '2px solid rgba(150, 160, 180, 0.5)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px'
          }}>⬇</span>
          <span>Mbps</span>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN COMPONENT =====
const SpeedTestPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { venueId } = useParams();

  // Venue data
  const [venue, setVenue] = useState(null);
  const [loadingVenue, setLoadingVenue] = useState(true);

  // Speed test states
  const [isTesting, setIsTesting] = useState(false);
  const [testProgress, setTestProgress] = useState(0);
  const [testStage, setTestStage] = useState('');
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [currentTestType, setCurrentTestType] = useState('idle');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [testDuration] = useState(10);

  // Speed results
  const [downloadSpeed, setDownloadSpeed] = useState(0.0);
  const [uploadSpeed, setUploadSpeed] = useState(0.0);
  const [pingMs, setPingMs] = useState(0);

  // Network info
  const [wifiInfo, setWifiInfo] = useState({
    connectionType: 'unknown',
    networkType: 'unknown',
    effectiveType: 'unknown',
    downlink: null,
    rtt: null,
    ssid: null,
  });

  // Network form states
  const [showNetworkForm, setShowNetworkForm] = useState(false);
  const [ssidInput, setSsidInput] = useState('');

  // Location states
  const [distanceFromVenue, setDistanceFromVenue] = useState(null);
  const [checkingLocation, setCheckingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [isWithinRange, setIsWithinRange] = useState(null);

  // Animation states
  const [needleAngle, setNeedleAngle] = useState(-90);
  const [isCompleted, setIsCompleted] = useState(false);

  // Refs
  const downloadWsRef = useRef(null);
  const uploadWsRef = useRef(null);
  const timeoutsRef = useRef([]);
  const intervalsRef = useRef([]);
  const testAbortedRef = useRef(false);
  const isMountedRef = useRef(true);
  const animationFrameRef = useRef(null);
  const targetAngleRef = useRef(-90);
  const currentAngleRef = useRef(-90);
  const animationStartTimeRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const lastProgressRef = useRef(0);
  const promptResolveRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const fetchVenue = async () => {
      if (!venueId) {
        setLoadingVenue(false);
        return;
      }

      try {
        console.log('🔄 Attempting to fetch venue:', venueId);
        const response = await venues.getById(venueId);
        setVenue(response.data?.data || response.data);
        console.log('✅ Venue loaded:', response.data);
        
        // Load history for venue
        if (response.data) {
          await loadHistoryFromDatabase(response.data);
        }
      } catch (err) {
        console.warn('⚠️ Could not load venue details (non-critical):', err.message);
        setVenue(null);
      } finally {
        setLoadingVenue(false);
      }
    };

    fetchVenue();
  }, [venueId]);

  useEffect(() => {
    isMountedRef.current = true;
    detectNetworkInfo();

    return () => {
      isMountedRef.current = false;
      cleanupAllResources();
    };
  }, []);

  // ===== HELPER FUNCTIONS =====
  const detectNetworkInfo = () => {
    try {
      let detectedInfo = {
        connectionType: 'unknown',
        networkType: 'unknown',
        effectiveType: 'unknown',
        downlink: null,
        rtt: null,
        ssid: null
      };

      if ('connection' in navigator) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        
        if (connection) {
          detectedInfo = {
            connectionType: connection.type || 'unknown',
            networkType: connection.type || 'unknown',
            effectiveType: connection.effectiveType || 'unknown',
            downlink: connection.downlink || null,
            rtt: connection.rtt || null,
            ssid: null
          };
          
          console.log('📶 Detected network info:', detectedInfo);
        }
      }

      if (window.location.protocol === 'https:') {
        detectedInfo.isLikelyWifi = true;
      }

      setWifiInfo(detectedInfo);
    } catch (error) {
      console.log('⚠️ Network detection error:', error);
    }
  };

  const getVenueCoordinates = () => {
    if (venue?.gpsData?.hl_confirmed_lat && venue?.gpsData?.hl_confirmed_lng) {
      return {
        lat: venue.gpsData.hl_confirmed_lat,
        lng: venue.gpsData.hl_confirmed_lng,
        source: 'GPS Confirmed'
      };
    }
    
    if (venue?.gpsData?.src_lat && venue?.gpsData?.src_lng) {
      return {
        lat: venue.gpsData.src_lat,
        lng: venue.gpsData.src_lng,
        source: 'GPS Source'
      };
    }
    
    if (venue?.lat && venue?.lng) {
      return {
        lat: venue.lat,
        lng: venue.lng,
        source: 'Venue coordinates'
      };
    }
    
    if (venue?.location?.coordinates && Array.isArray(venue.location.coordinates) && venue.location.coordinates.length === 2) {
      return {
        lat: venue.location.coordinates[1],
        lng: venue.location.coordinates[0],
        source: 'GeoJSON'
      };
    }
    
    return null;
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return distance;
  };

  const tryGetUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const errorMsg = 'Geolocation is not supported by your browser.';
        console.log(errorMsg);
        reject(new Error(errorMsg));
        return;
      }

      setCheckingLocation(true);
      setLocationError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLon = position.coords.longitude;
          
          const venueCoords = getVenueCoordinates();
          if (!venueCoords) {
            setCheckingLocation(false);
            resolve({ 
              allowed: true, 
              distance: null,
              location: { lat: userLat, lng: userLon, accuracy: position.coords.accuracy }
            });
            return;
          }

          const distance = calculateDistance(userLat, userLon, venueCoords.lat, venueCoords.lng);
          setDistanceFromVenue(distance);
          
          console.log(`📍 User location: ${userLat}, ${userLon}`);
          console.log(`📍 Venue location (${venueCoords.source}): ${venueCoords.lat}, ${venueCoords.lng}`);
          console.log(`📏 Distance: ${distance.toFixed(1)}m`);

          setIsWithinRange(true);
          setCheckingLocation(false);
          resolve({ 
            allowed: true, 
            distance,
            location: { lat: userLat, lng: userLon, accuracy: position.coords.accuracy }
          });
        },
        (error) => {
          let errorMsg = 'Unable to access your location. ';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMsg += 'Location access denied.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg += 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMsg += 'Location request timed out.';
              break;
            default:
              errorMsg += 'An unknown error occurred.';
          }
          
          console.log('Location error:', errorMsg);
          setCheckingLocation(false);
          setDistanceFromVenue(null);
          setLocationError(null);
          setIsWithinRange(false);
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const loadHistoryFromDatabase = async (venueData) => {
    try {
      const venueId = venueData?.id || venueData?._id;
      console.log('Loading history for venue', venueId);
      
      const response = await wifiSpeedAPI.getVenueSpeedTests(venueId);
      console.log('Raw API Response', response);

      if (response.data?.success && response.data.data) {
        const tests = response.data.data;
        console.log(`Loaded ${tests.length} speed tests from database`);
        
        setHistory(tests);
      } else {
        console.warn('Unexpected API response format', response.data);
        setHistory([]);
      }
    } catch (error) {
      console.error('Error loading history from database', error.response?.data || error.message);
      setHistory([]);
    }
  };

  const getLocalStats = () => {
    if (history.length === 0) return null;

    const avgDownload = (history.reduce((sum, t) => sum + t.downloadMbps, 0) / history.length).toFixed(1);
    const avgUpload = (history.reduce((sum, t) => sum + t.uploadMbps, 0) / history.length).toFixed(1);
    const avgLatency = Math.round(history.reduce((sum, t) => sum + t.latencyMs, 0) / history.length);
    const maxDownload = Math.max(...history.map(t => t.downloadMbps)).toFixed(1);

    return {
      totalTests: history.length,
      averageDownloadMbps: avgDownload,
      averageUploadMbps: avgUpload,
      averageLatencyMs: avgLatency,
      maxDownloadMbps: maxDownload
    };
  };

  const getQualityColor = (category) => {
    switch(category) {
      case 'excellent': return '#10b981';
      case 'good': return '#3b82f6';
      case 'fair': return '#f59e0b';
      case 'poor': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getQualityLabel = (category) => {
    switch(category) {
      case 'excellent': return `🚀 ${t('review.ratingExcellent')}`;
      case 'good': return `✅ ${t('review.ratingGood')}`;
      case 'fair': return `⚠️ ${t('review.ratingFair')}`;
      case 'poor': return `❌ ${t('review.ratingPoor')}`;
      default: return t('vd.unknown');
    }
  };

  // ===== ANIMATION FUNCTIONS =====
  const updateNeedleSmooth = useCallback((targetAngle) => {
    targetAngleRef.current = targetAngle;

    if (isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    animationStartTimeRef.current = Date.now();

    const animate = () => {
      const elapsed = Date.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / THROTTLE_CONFIG.animationDuration, 1);
      const easedProgress = THROTTLE_CONFIG.easingFunction(progress);

      const startAngle = currentAngleRef.current;
      const angleDiff = targetAngleRef.current - startAngle;
      const newAngle = startAngle + angleDiff * easedProgress;

      setNeedleAngle(newAngle);
      currentAngleRef.current = newAngle;

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isAnimatingRef.current = false;
        currentAngleRef.current = targetAngleRef.current;
        setNeedleAngle(targetAngleRef.current);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  const resetNeedle = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    isAnimatingRef.current = false;
    currentAngleRef.current = -90;
    targetAngleRef.current = -90;
    setNeedleAngle(-90);
    lastProgressRef.current = 0;
  }, []);

  // ===== CLEANUP FUNCTIONS =====
  const cleanupAllResources = () => {
    console.log('🧹 Cleaning up all resources...');

    if (downloadWsRef.current) {
      try {
        if (
          downloadWsRef.current.readyState === WebSocket.OPEN ||
          downloadWsRef.current.readyState === WebSocket.CONNECTING
        ) {
          downloadWsRef.current.close(1000, 'Cleanup');
        }
      } catch (e) {
        console.warn('Error closing download WebSocket:', e);
      }
      downloadWsRef.current = null;
    }

    if (uploadWsRef.current) {
      try {
        if (
          uploadWsRef.current.readyState === WebSocket.OPEN ||
          uploadWsRef.current.readyState === WebSocket.CONNECTING
        ) {
          uploadWsRef.current.close(1000, 'Cleanup');
        }
      } catch (e) {
        console.warn('Error closing upload WebSocket:', e);
      }
      uploadWsRef.current = null;
    }

    timeoutsRef.current.forEach((timeout) => {
      try {
        clearTimeout(timeout);
      } catch (e) {
        console.warn('Error clearing timeout:', e);
      }
    });
    timeoutsRef.current = [];

    intervalsRef.current.forEach((interval) => {
      try {
        clearInterval(interval);
      } catch (e) {
        console.warn('Error clearing interval:', e);
      }
    });
    intervalsRef.current = [];
  };

  const addTimeout = (callback, delay) => {
    const timeout = setTimeout(callback, delay);
    timeoutsRef.current.push(timeout);
    return timeout;
  };

  const addInterval = (callback, delay) => {
    const interval = setInterval(callback, delay);
    intervalsRef.current.push(interval);
    return interval;
  };

  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };

  // ===== NDT7 SPEED TEST FUNCTIONS =====
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const measureDownloadSpeed = (duration) => {
    return new Promise((resolve, reject) => {
      let lastMeasurement = 0;
      let measurements = [];
      let totalBytesReceived = 0;
      const startTime = Date.now();

      const wsUrl = `${NDT_WS_BASE}/ndt/v7/download`;
      console.log('📥 Starting NDT7 download test:', wsUrl);

      let ws;
      try {
        ws = new WebSocket(wsUrl, 'net.measurementlab.ndt.v7');
      } catch (e) {
        console.warn('Failed to create WebSocket with subprotocol, trying without:', e);
        try {
          ws = new WebSocket(wsUrl);
        } catch (err) {
          reject(new Error(t('wifi.wsConnectionFailed')));
          return;
        }
      }

      downloadWsRef.current = ws;
      ws.binaryType = 'arraybuffer';

      const testDurationMs = duration * 1000;
      let hasResolved = false;

      const resolveOnce = (value) => {
        if (!hasResolved && isMountedRef.current) {
          hasResolved = true;
          resolve(value);
        }
      };

      const rejectOnce = (error) => {
        if (!hasResolved && isMountedRef.current) {
          hasResolved = true;
          reject(error);
        }
      };

      ws.onopen = () => {
        if (testAbortedRef.current) {
          ws.close(1000, 'Aborted');
          rejectOnce(new Error('Test aborted'));
          return;
        }

        console.log('✅ Download WebSocket opened successfully');

        addTimeout(() => {
          if (!testAbortedRef.current && !hasResolved) {
            console.log(`⏱️ Download test completed (${duration} seconds)`);
            try {
              ws.close(1000, 'Test completed');
            } catch (e) {
              console.warn('Error closing WebSocket:', e);
            }
          }
        }, testDurationMs);
      };

      ws.onmessage = (event) => {
        if (testAbortedRef.current || hasResolved) return;

        if (event.data instanceof ArrayBuffer) {
          totalBytesReceived += event.data.byteLength;
          const elapsed = (Date.now() - startTime) / 1000;
          if (elapsed > 0) {
            const mbps = (totalBytesReceived * 8) / elapsed / 1000000;
            lastMeasurement = mbps;
            measurements.push(mbps);
            safeSetState(setCurrentSpeed, mbps);
          }
        }

        if (typeof event.data === 'string') {
          try {
            const measurement = JSON.parse(event.data);
            if (measurement.AppInfo && measurement.AppInfo.ElapsedTime) {
              const elapsedSeconds = measurement.AppInfo.ElapsedTime / 1000000;
              const numBytes = measurement.AppInfo.NumBytes || 0;
              if (elapsedSeconds > 0) {
                const mbps = (numBytes * 8) / elapsedSeconds / 1000000;
                lastMeasurement = mbps;
                measurements.push(mbps);
                safeSetState(setCurrentSpeed, mbps);
              }
            }
          } catch (e) {
            console.warn('Failed to parse measurement:', e);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('❌ Download WebSocket error:', error);
        if (testAbortedRef.current) {
          rejectOnce(new Error('Test aborted'));
          return;
        }

        if (lastMeasurement > 0 || totalBytesReceived > 0) {
          const elapsed = (Date.now() - startTime) / 1000;
          const mbps = elapsed > 0 ? (totalBytesReceived * 8) / elapsed / 1000000 : lastMeasurement;
          resolveOnce(Math.max(mbps, lastMeasurement));
        } else {
          rejectOnce(new Error(t('wifi.downloadConnectionError')));
        }
      };

      ws.onclose = (e) => {
        console.log(`🔌 Download WebSocket closed. Code: ${e.code}`);
        downloadWsRef.current = null;

        if (testAbortedRef.current) {
          rejectOnce(new Error('Test aborted'));
          return;
        }

        if (measurements.length > 0) {
          const recentMeasurements = measurements.slice(-5);
          const avgSpeed =
            recentMeasurements.reduce((a, b) => a + b, 0) / recentMeasurements.length;
          console.log(`📊 Final download speed: ${avgSpeed.toFixed(2)} Mbps`);
          resolveOnce(avgSpeed);
        } else if (totalBytesReceived > 0) {
          const elapsed = (Date.now() - startTime) / 1000;
          const mbps = elapsed > 0 ? (totalBytesReceived * 8) / elapsed / 1000000 : 0;
          resolveOnce(mbps);
        } else if (lastMeasurement > 0) {
          resolveOnce(lastMeasurement);
        } else {
          rejectOnce(new Error(t('wifi.downloadNoData')));
        }
      };

      addTimeout(() => {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          try {
            ws.close(1000, 'Timeout');
          } catch (e) {
            console.warn('Error closing WebSocket on timeout:', e);
          }
        }
      }, testDurationMs + 5000);
    });
  };

  const measureUploadSpeed = (duration) => {
    return new Promise((resolve, reject) => {
      let lastMeasurement = 0;
      let measurements = [];
      const startTime = Date.now();

      const wsUrl = `${NDT_WS_BASE}/ndt/v7/upload`;
      console.log('📤 Starting NDT7 upload test:', wsUrl);

      let ws;
      try {
        ws = new WebSocket(wsUrl, 'net.measurementlab.ndt.v7');
      } catch (e) {
        console.warn('Failed to create WebSocket with subprotocol, trying without:', e);
        try {
          ws = new WebSocket(wsUrl);
        } catch (err) {
          reject(new Error(t('wifi.wsConnectionFailed')));
          return;
        }
      }

      uploadWsRef.current = ws;
      ws.binaryType = 'arraybuffer';

      const chunkSize = 65536;
      const chunk = new Uint8Array(chunkSize);
      let uploadInterval = null;
      let totalBytesSent = 0;

      const testDurationMs = duration * 1000;
      let hasResolved = false;

      const resolveOnce = (value) => {
        if (!hasResolved && isMountedRef.current) {
          hasResolved = true;
          if (uploadInterval) {
            clearInterval(uploadInterval);
            uploadInterval = null;
          }
          resolve(value);
        }
      };

      const rejectOnce = (error) => {
        if (!hasResolved && isMountedRef.current) {
          hasResolved = true;
          if (uploadInterval) {
            clearInterval(uploadInterval);
            uploadInterval = null;
          }
          reject(error);
        }
      };

      ws.onopen = () => {
        if (testAbortedRef.current) {
          ws.close(1000, 'Aborted');
          rejectOnce(new Error('Test aborted'));
          return;
        }

        console.log('✅ Upload WebSocket opened successfully');

        addTimeout(() => {
          if (!testAbortedRef.current && !hasResolved) {
            console.log(`⏱️ Upload test completed (${duration} seconds)`);
            if (uploadInterval) clearInterval(uploadInterval);
            try {
              ws.close(1000, 'Test completed');
            } catch (e) {
              console.warn('Error closing WebSocket:', e);
            }
          }
        }, testDurationMs);

        uploadInterval = addInterval(() => {
          if (testAbortedRef.current || hasResolved) {
            if (uploadInterval) {
              clearInterval(uploadInterval);
              uploadInterval = null;
            }
            return;
          }

          if (ws.readyState !== WebSocket.OPEN) return;

          const currentBuffer = ws.bufferedAmount;
          const desiredBuffer = 8 * 1024 * 1024;

          if (currentBuffer < desiredBuffer) {
            const amountToSend = desiredBuffer - currentBuffer;
            const chunksToSend = Math.floor(amountToSend / chunkSize);

            for (let i = 0; i < chunksToSend; i++) {
              try {
                ws.send(chunk);
                totalBytesSent += chunkSize;
              } catch (e) {
                console.error('Upload send error:', e);
                break;
              }
            }

            const elapsed = (Date.now() - startTime) / 1000;
            if (elapsed > 0 && totalBytesSent > 0) {
              const mbps = (totalBytesSent * 8) / elapsed / 1000000;
              lastMeasurement = mbps;
              measurements.push(mbps);
              safeSetState(setCurrentSpeed, mbps);
            }
          }
        }, 100);
      };

      ws.onmessage = (event) => {
        if (testAbortedRef.current || hasResolved) return;

        if (typeof event.data === 'string') {
          try {
            const measurement = JSON.parse(event.data);
            if (
              measurement.TCPInfo &&
              measurement.TCPInfo.BytesReceived &&
              measurement.TCPInfo.ElapsedTime
            ) {
              const elapsedSeconds = measurement.TCPInfo.ElapsedTime / 1000000;
              const numBytes = measurement.TCPInfo.BytesReceived;
              if (elapsedSeconds > 0.5) {
                const mbps = (numBytes * 8) / elapsedSeconds / 1000000;
                lastMeasurement = mbps;
                measurements.push(mbps);
                safeSetState(setCurrentSpeed, mbps);
              }
            }

            if (measurement.AppInfo && measurement.AppInfo.ElapsedTime) {
              const elapsedSeconds = measurement.AppInfo.ElapsedTime / 1000000;
              const numBytes = measurement.AppInfo.NumBytes || 0;
              if (elapsedSeconds > 0) {
                const mbps = (numBytes * 8) / elapsedSeconds / 1000000;
                lastMeasurement = mbps;
                measurements.push(mbps);
                safeSetState(setCurrentSpeed, mbps);
              }
            }
          } catch (e) {
            console.warn('Failed to parse upload measurement:', e);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('❌ Upload WebSocket error:', error);
        if (testAbortedRef.current) {
          rejectOnce(new Error('Test aborted'));
          return;
        }

        if (totalBytesSent > 0) {
          const elapsed = (Date.now() - startTime) / 1000;
          const mbps = elapsed > 0 ? (totalBytesSent * 8) / elapsed / 1000000 : lastMeasurement;
          resolveOnce(Math.max(mbps, lastMeasurement));
        } else {
          resolveOnce(lastMeasurement || 0);
        }
      };

      ws.onclose = (e) => {
        console.log(`🔌 Upload WebSocket closed. Code: ${e.code}`);
        uploadWsRef.current = null;

        if (testAbortedRef.current) {
          rejectOnce(new Error('Test aborted'));
          return;
        }

        if (measurements.length > 0) {
          const recentMeasurements = measurements.slice(-5);
          const avgSpeed =
            recentMeasurements.reduce((a, b) => a + b, 0) / recentMeasurements.length;
          resolveOnce(avgSpeed);
        } else if (totalBytesSent > 0) {
          const elapsed = (Date.now() - startTime) / 1000;
          const mbps = elapsed > 0 ? (totalBytesSent * 8) / elapsed / 1000000 : 0;
          resolveOnce(mbps);
        } else if (lastMeasurement > 0) {
          resolveOnce(lastMeasurement);
        } else {
          resolveOnce(0);
        }
      };

      addTimeout(() => {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          if (uploadInterval) clearInterval(uploadInterval);
          try {
            ws.close(1000, 'Timeout');
          } catch (e) {
            console.warn('Error closing WebSocket on timeout:', e);
          }
        }
      }, testDurationMs + 5000);
    });
  };

  // ===== NETWORK FORM HANDLING =====
  const promptForNetworkInfo = () => {
    return new Promise(resolve => {
      setSsidInput('');
      setShowNetworkForm(true);
      promptResolveRef.current = resolve;
    });
  };

  const handleNetworkFormSubmit = async () => {
    if (!ssidInput?.trim()) {
      setError(t('wifi.enterNetworkNameError'));
      return;
    }
    
    const ssid = ssidInput.trim();
    setWifiInfo(prev => ({ ...prev, ssid }));
    setShowNetworkForm(false);
    setSsidInput('');

    if (promptResolveRef.current) {
      promptResolveRef.current(ssid);
      promptResolveRef.current = null;
    }

    await runSpeedTestWithNetworkInfo(ssid);
  };

  // ===== RUN SPEED TEST =====
  const runSpeedTest = async () => {
    if (isTesting) return;
    
    await promptForNetworkInfo();
  };

  const runSpeedTestWithNetworkInfo = async (ssid) => {
    if (isTesting) return;
    
    setIsTesting(true);
    setTestProgress(0);
    setCurrentSpeed(0);
    setError(null);
    setResults(null);
    setLocationError(null);
    testAbortedRef.current = false;
    resetNeedle();

    // Reset all values
    setDownloadSpeed(0.0);
    setUploadSpeed(0.0);
    setPingMs(0);
    setIsCompleted(false);

    try {
      // Optional location collection (non-blocking)
      let locationData = { allowed: true, distance: 0, location: null };
      
      if (venueId) {
        try {
          setTestStage(t('wifi.stageGettingLocation'));
          locationData = await tryGetUserLocation();
          console.log(`✅ Location collected: ${Math.round(locationData.distance)} meters from venue`);
        } catch (locationErr) {
          // Location error is non-blocking - just log it and continue
          console.log('⚠️ Location not available (continuing anyway):', locationErr.message);
          setLocationError(null); // Clear any location errors
          locationData = { allowed: true, distance: null, location: null };
        }
      }

      await sleep(300);
      if (testAbortedRef.current) throw new Error('Test aborted');
      setTestProgress(5);

      const location = locationData.location;
      setTestProgress(10);

      setTestStage(t('wifi.testingLatency'));
      const latencyStart = Date.now();
      try {
        await fetch(`${NDT_SERVER_BASE}/`, { method: 'HEAD', cache: 'no-cache' });
      } catch (e) {
        console.warn('Latency test warning:', e);
      }
      const latency = Date.now() - latencyStart;
      if (testAbortedRef.current) throw new Error('Test aborted');
      setPingMs(latency);
      setTestProgress(15);

      setTestStage(t('wifi.stageTestingDownloadSpeed'));
      setCurrentTestType('download');
      
      const downloadMbps = await measureDownloadSpeed(testDuration);
      setDownloadSpeed(downloadMbps.toFixed(2));
      console.log('📥 Download speed:', downloadMbps.toFixed(2), 'Mbps');

      if (testAbortedRef.current) throw new Error('Test aborted');
      setTestProgress(55);
      await sleep(500);

      setCurrentSpeed(0);
      setTestStage(t('wifi.stageTestingUploadSpeed'));
      setCurrentTestType('upload');
      
      const uploadMbps = await measureUploadSpeed(testDuration);
      setUploadSpeed(uploadMbps.toFixed(2));
      console.log('📤 Upload speed:', uploadMbps.toFixed(2), 'Mbps');

      if (testAbortedRef.current) throw new Error('Test aborted');
      setTestProgress(95);

      const qualityScore = calculateQualityScore(downloadMbps, uploadMbps, latency);
      const category = getCategory(qualityScore);

      const testResult = {
        testId: Date.now(),
        downloadMbps: parseFloat(downloadMbps.toFixed(2)),
        uploadMbps: parseFloat(uploadMbps.toFixed(2)),
        latencyMs: latency,
        qualityScore,
        category,
        testMethod: 'ndt7',
        notes: `SSID: ${ssid || 'Unknown'}`,
        timestamp: new Date().toISOString(),
        deviceInfo: getDeviceInfo(),
        location: location,
        distanceFromVenue: locationData.distance !== null ? Math.round(locationData.distance) : null,
        ssid: ssid,
        connectionType: wifiInfo.connectionType,
        networkType: wifiInfo.networkType,
        effectiveType: wifiInfo.effectiveType,
        downlink: wifiInfo.downlink,
        rtt: wifiInfo.rtt,
      };

      setTestProgress(100);
      setTestStage(t('wifi.stageComplete'));
      setResults(testResult);
      setIsCompleted(true);
      setCurrentTestType('idle');

      // Save to backend if venue is available
      if (venueId) {
        try {
          setTestStage(t('wifi.savingResults'));
          const backendData = {
            venueId: venue?.id || venue?._id || venueId,
            downloadMbps: testResult.downloadMbps,
            uploadMbps: testResult.uploadMbps,
            latencyMs: testResult.latencyMs,
            jitterMs: 0,
            packetLoss: 0,
            connectionType: wifiInfo.connectionType,
            deviceInfo: { ...testResult.deviceInfo, platform: 'consumer-pwa' },
            testMethod: 'ndt7',
            testServer: NDT_SERVER_BASE,
            location: testResult.location,
            notes: testResult.notes,
            ssid: ssid,
            networkType: wifiInfo.networkType,
            effectiveType: wifiInfo.effectiveType,
            downlink: wifiInfo.downlink,
            rtt: wifiInfo.rtt,
            testDurationS: testDuration,   // EV-632 — record duration (backend already parses it)
          };

          console.log('📤 Submitting speed test to backend:', backendData);

          if (!backendData.venueId) {
            throw new Error(t('wifi.missingVenueId'));
          }

          const response = await wifiSpeedAPI.submitSpeedTest(backendData);
          console.log('✅ Speed test submitted successfully:', response.data);

          await loadHistoryFromDatabase(venue || { id: venueId });

          setTestStage(t('wifi.stageComplete'));
          
        } catch (submitError) {
          console.error('⚠️ Error in save process:', submitError);
          setError(t('wifi.syncFailedAfterSuccess'));
        }
      }

      setTestStage(t('wifi.stageComplete'));
    } catch (e) {
      console.error('❌ Speed test error:', e);
      if (testAbortedRef.current || e.message === 'Test aborted') {
        safeSetState(setError, t('wifi.testStoppedByUser'));
      } else {
        safeSetState(setError, e?.message || t('wifi.testFailedGeneric'));
      }
    } finally {
      cleanupAllResources();
      safeSetState(setIsTesting, false);
      safeSetState(setCurrentSpeed, 0);
    }
  };

  // ===== STOP TEST =====
  const stopTest = () => {
    if (!isTesting) return;

    console.log('⏹️ Stopping test manually...');
    testAbortedRef.current = true;
    cleanupAllResources();
    safeSetState(setIsTesting, false);
    safeSetState(setCurrentSpeed, 0);
    safeSetState(setTestProgress, 0);
    safeSetState(setTestStage, '');
    safeSetState(setResults, null);
    safeSetState(setError, t('wifi.testStoppedByUser'));

    addTimeout(() => {
      testAbortedRef.current = false;
    }, 500);
  };

  // ===== RESET TEST =====
  const resetTest = () => {
    setResults(null);
    setError(null);
    setTestProgress(0);
    setCurrentSpeed(0);
    setCurrentTestType('idle');
    setTestStage('');
    setIsTesting(false);
    setIsCompleted(false);
    setDownloadSpeed(0.0);
    setUploadSpeed(0.0);
    setPingMs(0);
    resetNeedle();
  };

  // ===== RENDER =====
  if (loadingVenue) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)'
      }}>
        <div style={{ color: 'white', fontSize: '20px', fontWeight: '600' }}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  const stats = getLocalStats();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Network Information Form Modal */}
      {showNetworkForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
        }}>
          <motion.div 
            style={{
              background: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              padding: '25px',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '450px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: 'white',
              marginBottom: '15px',
              textAlign: 'center'
            }}>
              {t('wifi.networkNameHeading')}
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '20px',
              textAlign: 'center',
            }}>
              {t('wifi.networkNameSubtitle')}
            </p>

            <input 
              type="text" 
              value={ssidInput} 
              onChange={(e) => setSsidInput(e.target.value)}
              placeholder={t('wifi.ssidInputPlaceholder')}
              style={{
                width: '100%',
                padding: '15px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '2px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '12px',
                fontSize: '16px',
                color: 'white',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
              autoFocus 
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleNetworkFormSubmit();
                }
              }}
            />

            <div style={{ display: 'flex', gap: '15px' }}>
              <motion.button 
                onClick={handleNetworkFormSubmit}
                style={{ 
                  flex: 1,
                  padding: '15px',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0097a7 100%)',
                  color: 'white'
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('wifi.startTest')}
              </motion.button>
              <motion.button 
                onClick={() => {
                  setShowNetworkForm(false);
                  setSsidInput('');
                }}
                style={{ 
                  flex: 1,
                  padding: '15px',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('common.cancel')}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Enhanced Background with Network Visualization */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            animation: isTesting ? 'networkFlow 3s linear infinite' : 'none',
          }}
        />

        {isTesting &&
          [...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: '4px',
                height: '4px',
                backgroundColor: '#fbbf24',
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                x: [0, (Math.random() - 0.5) * 200],
                y: [0, (Math.random() - 0.5) * 200],
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
      </div>

      {/* Header */}
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        style={{
          position: 'relative',
          zIndex: 50,
          padding: '16px 20px',
          background: 'rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
            }}
          >
            <ArrowLeftIcon style={{ height: '24px', width: '24px', color: 'white' }} />
          </motion.button>

          <div style={{ textAlign: 'center', flex: 1 }}>
            <h1
              style={{
                fontSize: '20px',
                fontWeight: '700',
                color: 'white',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <WifiIcon style={{ height: '24px', width: '24px', color: 'white' }} />
              {t('wifi.pageTitle')}
            </h1>
            {venue && venue.name && (
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', margin: '4px 0 0 0' }}>
                {venue.name}
              </p>
            )}
          </div>

          <div style={{ width: '44px' }} />
        </div>
      </motion.div>

      {/* Main Content */}
      <div style={{ 
        position: 'relative', 
        zIndex: 10, 
        padding: '20px',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'rgba(239, 68, 68, 0.9)',
              color: 'white',
              padding: '16px',
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              fontSize: '14px',
              textAlign: 'center'
            }}
          >
            {error}
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {/* Test Running State */}
          {isTesting && !results && (
            <motion.div
              key="testing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SpeedometerGauge 
                value={currentSpeed} 
                maxValue={1000}
                label={currentTestType === 'download' ? t('wifi.download') : t('wifi.upload')}
              />

              {/* Test Progress Bar */}
              <div style={{
                width: '100%',
                height: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '16px'
              }}>
                <motion.div
                  style={{
                    height: '100%',
                    background: 'linear-gradient(90deg, #00d4ff, #0097a7)',
                    borderRadius: '4px',
                  }}
                  initial={{ width: '0%' }}
                  animate={{ width: `${testProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              <div style={{
                textAlign: 'center',
                color: 'white',
                fontSize: '16px',
                marginBottom: '20px'
              }}>
                {testStage} ({testProgress}%)
              </div>

              {/* Stop Button */}
              <motion.button
                onClick={stopTest}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: 'rgba(239, 68, 68, 0.9)',
                  color: 'white',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  margin: '0 auto',
                }}
              >
                <StopCircleIcon style={{ height: '20px', width: '20px' }} />
                {t('wifi.stopTest')}
              </motion.button>
            </motion.div>
          )}

          {/* Idle State - Start Test */}
          {!isTesting && !results && (
            <motion.div
              key="idle"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                padding: '40px 24px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <WifiIcon style={{ 
                  width: '80px', 
                  height: '80px', 
                  color: 'white', 
                  margin: '0 auto 20px' 
                }} />
                
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  {t('wifi.testYourSpeedTitle')}
                </h2>
                
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  marginBottom: '30px',
                  lineHeight: '1.6'
                }}>
                  {t('wifi.testYourSpeedSubtitle')}
                </p>

                {/* Native app banner — Android only */}
                {/Android/i.test(navigator.userAgent) && (
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    borderRadius: '12px',
                    padding: '14px 16px',
                    marginBottom: '20px',
                    textAlign: 'center',
                  }}>
                    <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', marginBottom: '10px', lineHeight: '1.4' }}>
                      {t('wifi.nativeAppBannerText')}
                    </p>
                    <button
                      onClick={() => {
                        const slug = venue?.slug || venueId || '';
                        const deepLink = slug
                          ? `honestlee://wifi/speed-test?venue=${slug}&mode=consumer`
                          : 'honestlee://wifi/speed-test';
                        window.location.href = deepLink;
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {t('wifi.openInApp')}
                    </button>
                    <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>
                      {t('wifi.orRunBrowserTest')}
                    </p>
                  </div>
                )}

                <motion.button
                  onClick={runSpeedTest}
                  whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)' }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '18px 40px',
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0097a7 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    fontWeight: '700',
                    fontSize: '18px',
                    border: 'none',
                    cursor: 'pointer',
                    margin: '0 auto',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <SignalIcon style={{ height: '24px', width: '24px' }} />
                  {t('wifi.startSpeedTest')}
                </motion.button>
              </div>

              {/* Display Stats if available */}
              {stats && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    marginBottom: '20px'
                  }}
                >
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <ChartBarIcon style={{ width: '20px', height: '20px' }} />
                    {t('wifi.previousTests', { count: stats.totalTests })}
                  </h3>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '12px'
                  }}>
                    <div style={{
                      background: 'rgba(16, 185, 129, 0.2)',
                      padding: '12px',
                      borderRadius: '10px',
                      border: '1px solid rgba(16, 185, 129, 0.3)'
                    }}>
                      <div style={{ color: '#10b981', fontSize: '12px', marginBottom: '4px' }}>
                        {t('wifi.avgDownload')}
                      </div>
                      <div style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>
                        {stats.averageDownloadMbps} Mbps
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(59, 130, 246, 0.2)',
                      padding: '12px',
                      borderRadius: '10px',
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      <div style={{ color: '#3b82f6', fontSize: '12px', marginBottom: '4px' }}>
                        {t('wifi.avgUpload')}
                      </div>
                      <div style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>
                        {stats.averageUploadMbps} Mbps
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(251, 191, 36, 0.2)',
                      padding: '12px',
                      borderRadius: '10px',
                      border: '1px solid rgba(251, 191, 36, 0.3)'
                    }}>
                      <div style={{ color: '#fbbf24', fontSize: '12px', marginBottom: '4px' }}>
                        {t('wifi.avgLatency')}
                      </div>
                      <div style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>
                        {stats.averageLatencyMs} ms
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(168, 85, 247, 0.2)',
                      padding: '12px',
                      borderRadius: '10px',
                      border: '1px solid rgba(168, 85, 247, 0.3)'
                    }}>
                      <div style={{ color: '#a855f7', fontSize: '12px', marginBottom: '4px' }}>
                        {t('wifi.maxDownload')}
                      </div>
                      <div style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>
                        {stats.maxDownloadMbps} Mbps
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Recent History */}
              {history.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '16px'
                  }}>
                    {t('vs.recentTests')}
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {history.slice(0, 5).map((test, index) => (
                      <div
                        key={test.testId || index}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: '12px',
                          borderRadius: '10px',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{ 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            fontSize: '12px' 
                          }}>
                            {new Date(test.timestamp).toLocaleDateString()} {new Date(test.timestamp).toLocaleTimeString()}
                          </span>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: getQualityColor(test.category || getCategory(test.qualityScore || calculateQualityScore(test.downloadMbps, test.uploadMbps, test.latencyMs))),
                            color: 'white'
                          }}>
                            {getQualityLabel(test.category || getCategory(test.qualityScore || calculateQualityScore(test.downloadMbps, test.uploadMbps, test.latencyMs)))}
                          </span>
                        </div>
                        
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '8px',
                          fontSize: '13px'
                        }}>
                          <div>
                            <span style={{ color: '#10b981' }}>⬇ </span>
                            <span style={{ color: 'white', fontWeight: '600' }}>
                              {test.downloadMbps.toFixed(1)} Mbps
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#3b82f6' }}>⬆ </span>
                            <span style={{ color: 'white', fontWeight: '600' }}>
                              {test.uploadMbps.toFixed(1)} Mbps
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#fbbf24' }}>🕐 </span>
                            <span style={{ color: 'white', fontWeight: '600' }}>
                              {test.latencyMs} ms
                            </span>
                          </div>
                        </div>
                        
                        {test.ssid && (
                          <div style={{
                            fontSize: '11px',
                            color: 'rgba(255, 255, 255, 0.5)',
                            marginTop: '6px'
                          }}>
                            📶 {test.ssid}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Results Display */}
          {results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              {/* Results Summary Card */}
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '20px',
                  paddingTop: '48px',
                  paddingBottom: '24px',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(20px)',
                  marginBottom: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Quality Score */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    padding: '12px 24px', 
                    borderRadius: '50px', 
                    color: 'white', 
                    fontWeight: '700',
                    textAlign: 'center',
                    marginBottom: '16px',
                    fontSize: '18px',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                    background: getQualityColor(results.category)
                  }}>
                    {getQualityLabel(results.category)}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', textAlign: 'center' }}>
                    {t('wifi.qualityScoreLabel')} <span style={{ color: '#fbbf24', fontWeight: '700' }}>{results.qualityScore}/100</span>
                  </div>
                </div>

                {/* Speed Quality Indicators */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    marginTop: '20px',
                    marginBottom: '20px',
                  }}
                >
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(16, 185, 129, 0.2)',
                      border: '1px solid rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    <div style={{ color: '#10b981', fontSize: '24px', marginBottom: '4px' }}>
                      {getSpeedQuality(results.downloadMbps, t).emoji}
                    </div>
                    <div style={{ color: 'white', fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>
                      {results.downloadMbps} Mbps
                    </div>
                    <div style={{ color: 'white', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      {t('wifi.download')}
                    </div>
                    <div
                      style={{
                        color: getSpeedQuality(results.downloadMbps, t).color,
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      {getSpeedQuality(results.downloadMbps, t).label}
                    </div>
                  </div>

                  <div
                    style={{
                      textAlign: 'center',
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                      border: '1px solid rgba(59, 130, 246, 0.4)',
                    }}
                  >
                    <div style={{ color: '#3b82f6', fontSize: '24px', marginBottom: '4px' }}>
                      {getSpeedQuality(results.uploadMbps, t).emoji}
                    </div>
                    <div style={{ color: 'white', fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>
                      {results.uploadMbps} Mbps
                    </div>
                    <div style={{ color: 'white', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      {t('wifi.upload')}
                    </div>
                    <div
                      style={{
                        color: getSpeedQuality(results.uploadMbps, t).color,
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      {getSpeedQuality(results.uploadMbps, t).label}
                    </div>
                  </div>
                </div>

                {/* Ping */}
                {pingMs > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '16px',
                      color: 'white',
                      marginBottom: '16px',
                    }}
                  >
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '12px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(251, 191, 36, 0.2)',
                        border: '1px solid rgba(251, 191, 36, 0.4)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '6px',
                        }}
                      >
                        <ClockIcon
                          style={{ width: '16px', height: '16px', marginRight: '4px', color: '#fbbf24' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#fbbf24' }}>
                          {t('wifi.pingLabel')}
                        </span>
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>
                        {pingMs} ms
                      </div>
                    </div>
                  </div>
                )}

                {/* Network Details */}
                {results.ssid && (
                  <motion.div 
                    style={{
                      background: 'rgba(0, 212, 255, 0.1)',
                      padding: '12px',
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      marginBottom: '16px',
                      textAlign: 'left'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <WifiIcon style={{ width: '16px', height: '16px', color: '#00d4ff' }} />
                      <strong style={{ color: '#00d4ff', fontSize: '14px' }}>{t('wifi.networkLabel')}</strong>
                    </div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)' }}>
                      {results.ssid}
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Run New Test Button */}
              <motion.button
                onClick={resetTest}
                whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1))',
                  color: 'white',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  cursor: 'pointer',
                  backdropFilter: 'blur(10px)',
                  margin: '0 auto',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                }}
              >
                <ArrowPathIcon style={{ height: '20px', width: '20px' }} />
                {t('wifi.runNewTest')}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SpeedTestPage;
